import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageSEO from "@/components/feature/PageSEO";

export default function PrivacyPage() {
  return (
    <main>
      <PageSEO
        title="Privacy Policy | PixelCraft Agency"
        description="Read the Privacy Policy for PixelCraft Agency. Learn how we collect, use, and protect your personal data in line with UK GDPR."
        canonical="/privacy"
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-emerald-950 text-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <p className="text-emerald-400 text-xs font-semibold uppercase tracking-widest mb-3">Legal</p>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-emerald-200/70 text-sm">Last updated: April 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="prose prose-zinc max-w-none text-zinc-700 text-sm leading-relaxed space-y-10">

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">1. Who We Are</h2>
              <p>
                PixelCraft Agency ("we", "us", "our") is a UK-based web design service for small businesses. We are committed to protecting your personal data and complying with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
              </p>
              <p className="mt-2">
                For any privacy-related queries, contact us at{" "}
                <a href="mailto:hello@pixelcraft.agency" className="text-emerald-700 hover:text-emerald-900 underline">
                  hello@pixelcraft.agency
                </a>.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">2. What Data We Collect</h2>
              <p>We may collect the following personal data:</p>
              <ul className="list-disc pl-5 mt-3 space-y-1">
                <li><strong>Contact information:</strong> name, email address, phone number, business name</li>
                <li><strong>Payment information:</strong> billing address (payment card details are processed securely by our payment provider and not stored by us)</li>
                <li><strong>Website usage data:</strong> IP address, browser type, pages visited, via cookies and analytics tools</li>
                <li><strong>Communications:</strong> emails, messages, and enquiries you send us</li>
              </ul>
            </div>

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">3. How We Use Your Data</h2>
              <p>We use your personal data to:</p>
              <ul className="list-disc pl-5 mt-3 space-y-1">
                <li>Provide and manage the services you have requested</li>
                <li>Process payments and send invoices</li>
                <li>Communicate with you about your project or account</li>
                <li>Send service-related updates and renewal reminders</li>
                <li>Improve our website and services through analytics</li>
                <li>Comply with legal obligations</li>
              </ul>
            </div>

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">4. Legal Basis for Processing</h2>
              <p>We process your data on the following legal bases:</p>
              <ul className="list-disc pl-5 mt-3 space-y-1">
                <li><strong>Contract:</strong> to fulfil our service agreement with you</li>
                <li><strong>Legitimate interests:</strong> to improve our services and communicate with you</li>
                <li><strong>Legal obligation:</strong> to comply with applicable laws</li>
                <li><strong>Consent:</strong> where you have opted in to marketing communications</li>
              </ul>
            </div>

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">5. Cookies</h2>
              <p>
                Our website uses cookies to improve your browsing experience and gather analytics data. You can control cookie settings through your browser. By continuing to use our website, you consent to our use of cookies in accordance with this policy.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">6. Data Sharing</h2>
              <p>
                We do not sell your personal data. We may share it with trusted third-party service providers (e.g. payment processors, hosting providers, analytics tools) solely to deliver our services. All third parties are required to handle your data securely and in accordance with applicable law.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">7. Data Retention</h2>
              <p>
                We retain your personal data for as long as necessary to provide our services and comply with legal obligations. Client records are typically retained for 6 years in line with UK tax and accounting requirements.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">8. Your Rights</h2>
              <p>Under UK GDPR, you have the right to:</p>
              <ul className="list-disc pl-5 mt-3 space-y-1">
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data ("right to be forgotten")</li>
                <li>Object to or restrict processing of your data</li>
                <li>Data portability</li>
                <li>Withdraw consent at any time (where processing is based on consent)</li>
              </ul>
              <p className="mt-3">
                To exercise any of these rights, please contact us at{" "}
                <a href="mailto:hello@pixelcraft.agency" className="text-emerald-700 hover:text-emerald-900 underline">
                  hello@pixelcraft.agency
                </a>. We will respond within 30 days.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">9. Security</h2>
              <p>
                We take appropriate technical and organisational measures to protect your personal data against unauthorised access, loss, or disclosure. However, no internet transmission is completely secure, and we cannot guarantee absolute security.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">10. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of significant changes by email or by posting a notice on our website. The "Last updated" date at the top of this page reflects the most recent revision.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">11. Complaints</h2>
              <p>
                If you are unhappy with how we handle your data, you have the right to lodge a complaint with the Information Commissioner&apos;s Office (ICO) at{" "}
                <a href="https://ico.org.uk" rel="nofollow" target="_blank" className="text-emerald-700 hover:text-emerald-900 underline">
                  ico.org.uk
                </a>.
              </p>
            </div>

          </div>

          {/* Back link */}
          <div className="mt-14 pt-8 border-t border-zinc-100 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <a
              href="/terms"
              className="text-sm text-emerald-700 hover:text-emerald-900 underline cursor-pointer"
            >
              Read our Terms of Service
            </a>
            <a
              href="/contact"
              className="ml-auto whitespace-nowrap bg-accent hover:bg-accent-dark text-white text-sm font-bold px-5 py-2.5 rounded-lg transition-all cursor-pointer"
            >
              Get in Touch
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
